import { useCallback, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';
import { RestError, useAuthedRest } from '@/api/useAuthedRest';

import { useGraphQLMutation } from './useGraphQLMutation';
import { useGraphQLQuery } from './useGraphQLQuery';

// Mirrors AllowlistEntryResponse from the API
// (apps/api-cf/src/backstage/allowlist/allowlist.service.ts). `name`/`surname` are
// non-null when a registered user exists for the email.
export interface AllowlistEntry {
  id: string;
  email: string;
  addedAt: string;
  name: string | null;
  surname: string | null;
}

// The API's duplicate-add 400 body (ALREADY_ON_LIST_MESSAGE in
// apps/api-cf/src/backstage/allowlist/allowlist.service.ts) — matched verbatim to
// render the inline "Already on the list" error. Keep in sync.
export const ALREADY_ON_LIST_MESSAGE = 'Already on the list';

const GENERIC_ADD_ERROR = "Couldn't add — try again";

interface UseAllowlistReturn {
  entries: AllowlistEntry[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<AllowlistEntry[] | null>;
}

// The useGraphQLQuery hook is transport-agnostic — it only drives a
// fetcher with abort/redirect handling — so the REST admin endpoint
// reuses it rather than duplicating that machinery.
export function useAllowlist(): UseAllowlistReturn {
  const rest = useAuthedRest();

  const { data, isLoading, error, refetch } = useGraphQLQuery<AllowlistEntry[]>({
    fetcher: (signal) => rest.request<AllowlistEntry[]>('/admin/allowlist', { signal }),
    deps: [],
  });

  return { entries: data, isLoading, error, refetch };
}

// `message: null` means an Auth0 redirect is in flight — the browser is
// about to navigate to Universal Login, so render no error.
export type AddAllowlistResult =
  | { ok: true; entry: AllowlistEntry }
  | { ok: false; message: string | null };

interface UseAddAllowlistEntryReturn {
  addEntry: (email: string) => Promise<AddAllowlistResult>;
  isSubmitting: boolean;
}

// Returns a result union instead of the error-state pattern because the
// add form needs the failure reason synchronously (the duplicate 400 is
// an expected, inline-rendered outcome, not exceptional error UI).
export function useAddAllowlistEntry(): UseAddAllowlistEntryReturn {
  const rest = useAuthedRest();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addEntry = useCallback(
    async (email: string): Promise<AddAllowlistResult> => {
      setIsSubmitting(true);
      try {
        const entry = await rest.request<AllowlistEntry>('/admin/allowlist', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        return { ok: true, entry };
      } catch (err) {
        if (err instanceof AuthRedirectError) {
          return { ok: false, message: null };
        }
        if (err instanceof RestError && err.status === 400) {
          return {
            ok: false,
            message: err.message === ALREADY_ON_LIST_MESSAGE ? err.message : GENERIC_ADD_ERROR,
          };
        }
        return { ok: false, message: GENERIC_ADD_ERROR };
      } finally {
        setIsSubmitting(false);
      }
    },
    [rest],
  );

  return { addEntry, isSubmitting };
}

interface UseRemoveAllowlistEntryReturn {
  removeEntry: (id: string) => Promise<boolean>;
  isSubmitting: boolean;
  error: Error | null;
}

export function useRemoveAllowlistEntry(): UseRemoveAllowlistEntryReturn {
  const rest = useAuthedRest();

  const { mutate, isSubmitting, error } = useGraphQLMutation<string, true>({
    mutationFn: async (id, signal) => {
      await rest.request<undefined>(`/admin/allowlist/${id}`, { method: 'DELETE', signal });
      return true;
    },
  });

  const removeEntry = useCallback(async (id: string) => (await mutate(id)) === true, [mutate]);

  return { removeEntry, isSubmitting, error };
}
